  // src/features/invoice-tracker/components/tabs/SalesOrder.jsx
  import { useEffect, useMemo, useRef, useState, useCallback } from "react";
  import { createPortal } from "react-dom";
import {
  Plus,
  Search,
  Eye,
  MoreVertical,
  Copy,
  FileDown,
  X,
  Ban,
  Wallet,
  ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";

  import { BASE_URL } from "shared/lib/config";
 
  import SelectInvoiceForm from "../others/CreateSalesForm";
  import AdvanceViewModal from "../others/AdvanceViewModal";
  import PaymentRestructureForm from "../others/PaymentRestructureForm";


  /* =========================================================
  * Helpers
  * ========================================================= */
 
  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return localStorage;
  };
  const getLatestReferenceDoc = (sale) => {
  const arr = Array.isArray(sale?.reference_documents) ? sale.reference_documents : [];
  if (!arr.length) return sale?.reference_document || null;
  const lastWithPath = [...arr].reverse().find((d) => d && d.storage_path);
  return lastWithPath || arr[arr.length - 1] || sale?.reference_document || null;
};

  const getToken = () => getStore().getItem("idToken");

  async function fetchJson(url, opts = {}) {
    const token = getToken();
    const headers = {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, { ...opts, headers });
    const contentType = res.headers.get("content-type") || "";

    let data = null;
    if (contentType.includes("application/json")) data = await res.json().catch(() => null);
    else data = await res.text().catch(() => null);

    if (!res.ok) {
      const msg = data?.message || (typeof data === "string" && data) || `Request failed (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  async function fetchBlob(url, opts = {}) {
    const token = getToken();
    const headers = {
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, { ...opts, headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Request failed (${res.status})`);
    }
    return await res.blob();
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(
      Number(amount || 0)
    );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  };

  const DOC_TYPE = ["PO", "Quotation", "MOU"];

  const SALES_STATUS = [
    { value: "unpaid", label: "Unpaid" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "paid", label: "Paid" },
    { value: "cancelled", label: "Cancelled" },
  ];

  function normalizeStatus(v) {
    const s = String(v || "").trim().toLowerCase();
    if (!s) return "unpaid";
    if (s === "partial" || s === "partiallypaid" || s === "partially_paid") return "partially_paid";
    if (s === "paid") return "paid";
    if (s === "unpaid") return "unpaid";
    if (s === "cancel" || s === "cancelled") return "cancelled";
    return s;
  }

  function statusBadgeClass(status) {
    const s = normalizeStatus(status);
    if (s === "paid") return "bg-emerald-100 text-emerald-800";
    if (s === "partially_paid") return "bg-amber-100 text-amber-800";
    if (s === "cancelled") return "bg-rose-100 text-rose-800";
    return "bg-slate-100 text-slate-700";
  }

  function statusLabel(status) {
    const s = normalizeStatus(status);
    const found = SALES_STATUS.find((x) => x.value === s);
    return found?.label || (s ? s.replace(/_/g, " ") : "Unpaid");
  }

  const buildDownloadDocUrl = (saleId) => `${BASE_URL}/api/invoice-tracker/sales/${saleId}/proof/download`;
  const buildDownloadInvoiceSentUrl = (invoiceId) => `${BASE_URL}/api/invoice-tracker/invoices/${invoiceId}/sent/download`;
  const buildDownloadInvoicePaymentUrl = (invoiceId) =>
    `${BASE_URL}/api/invoice-tracker/invoices/${invoiceId}/payment/download`;

  const buildRestructurePutUrl = (saleId) => `${BASE_URL}/api/invoice-tracker/sales/${saleId}/invoices/restructure`;

  async function putToSignedUrl({ signedUrl, file }) {
    const res = await fetch(signedUrl, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: file });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Upload to signedUrl failed (${res.status})`);
    }
    return true;
  }

  async function uploadSaleProofPdf({ saleId, file }) {
    if (!saleId) throw new Error("saleId missing");
    if (!file) throw new Error("file missing");

    const isPdf =
      String(file.type || "").toLowerCase() === "application/pdf" ||
      String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) throw new Error("Hanya PDF yang diperbolehkan untuk bukti sales (proof).");

    const attemptOnce = async () => {
      const signed = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}/proof/signed-url`, {
        method: "POST",
        body: JSON.stringify({ fileName: "reference.pdf", contentType: "application/pdf" }),
      });

      const signedUrl = signed?.signedUrl;
      const objectPath = signed?.objectPath;
      if (!signedUrl || !objectPath) throw new Error("Signed URL response invalid (missing signedUrl/objectPath)");

      await putToSignedUrl({ signedUrl, file });

      await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}/proof/commit`, {
        method: "POST",
        body: JSON.stringify({ objectPath }),
      });

      return true;
    };

    try {
      await attemptOnce();
      return true;
    } catch (err) {
      const msg = String(err?.message || "").toLowerCase();
      const maybeExpired =
        msg.includes("expired") || msg.includes("signature") || msg.includes("request time") || msg.includes("forbidden") || msg.includes("403");
      if (!maybeExpired) throw err;
      await attemptOnce();
      return true;
    }
  }

  /* =========================================================
  * Small hooks: debounce + simple cache
  * ========================================================= */
  function useDebouncedValue(value, delayMs) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), Math.max(0, delayMs || 0));
      return () => clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
  }

  const SALES_CACHE_KEY = "invoice_tracker_sales_cache_v2";
  const CACHE_TTL_MS = 60_000;

function clearSalesCache() {
  try {
    sessionStorage.removeItem(SALES_CACHE_KEY);
  } catch {}
}

  function loadSalesCache() {
    try {
      const raw = sessionStorage.getItem(SALES_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch {
      return null;
    }
  }

  function saveSalesCache(payload) {
    try {
      sessionStorage.setItem(SALES_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }

  /* =========================================================
  * Dropdown Menu (Portal) - responsive
  * ========================================================= */
  function RowMenuPortal({ open, anchorRect, onClose, children }) {
    const menuRef = useRef(null);

    useEffect(() => {
      if (!open) return;

      const onKeyDown = (e) => e.key === "Escape" && onClose?.();

      const onMouseDown = (e) => {
        const el = menuRef.current;
        if (!el) return;
        if (!el.contains(e.target)) onClose?.();
      };

      const onScroll = () => onClose?.();
      const onResize = () => onClose?.();

      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("mousedown", onMouseDown);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onResize);

      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onResize);
      };
    }, [open, onClose]);

    if (!open || !anchorRect) return null;

    const gap = 6;
    const width = 200;
    const left = Math.max(8, Math.min(window.innerWidth - width - 8, anchorRect.right - width));
    const top = Math.min(window.innerHeight - 12, anchorRect.bottom + gap);

    return createPortal(
      <div className="fixed inset-0 z-[9999]">
        <div
          ref={menuRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="absolute w-[200px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          style={{ left, top }}
        >
          {children}
        </div>
      </div>,
      document.body
    );
  }

  /* =========================================================
  * Multi Select Dropdown (Checkbox) - prettier
  * ========================================================= */
  function MultiSelectDropdown({ label, placeholder, options, value, onChange, minWidthClass = "sm:min-w-[140px]" }) {
    const [open, setOpen] = useState(false);
    const [anchorRect, setAnchorRect] = useState(null);
    const btnRef = useRef(null);
    const panelRef = useRef(null);

    const normalizedOptions = useMemo(() => {
      if (!Array.isArray(options)) return [];
      if (options.length === 0) return [];
      if (typeof options[0] === "string") {
        return options.map((s) => ({ value: s, label: s }));
      }
      return options.map((o) => ({ value: String(o.value), label: String(o.label ?? o.value) }));
    }, [options]);

    const selectedSet = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
    const selectedCount = Array.isArray(value) ? value.length : 0;

    const buttonText = useMemo(() => {
      if (selectedCount === 0) return placeholder || label || "Select";
      if (label) return `${label}: ${selectedCount}`;
      return `${selectedCount} selected`;
    }, [selectedCount, placeholder, label]);

    const toggleOpen = () => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setAnchorRect(rect);
      setOpen((v) => !v);
    };

    const close = () => setOpen(false);

    useEffect(() => {
      if (!open) return;

      const onKeyDown = (e) => e.key === "Escape" && close();
      const onMouseDown = (e) => {
        const p = panelRef.current;
        const b = btnRef.current;
        if (!p || !b) return;
        if (p.contains(e.target) || b.contains(e.target)) return;
        close();
      };
      const onScroll = () => close();
      const onResize = () => close();

      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("mousedown", onMouseDown);
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onResize);

      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onResize);
      };
    }, [open]);

    const setAll = () => onChange?.(normalizedOptions.map((o) => o.value));
    const clearAll = () => onChange?.([]);

    const toggleValue = (v) => {
      const next = new Set(selectedSet);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      onChange?.(Array.from(next));
    };

    if (!normalizedOptions.length) {
      return (
        <button
          type="button"
          disabled
          className={`w-full sm:w-auto ${minWidthClass} px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-400 bg-gray-50 cursor-not-allowed`}
        >
          {placeholder || label || "Select"}
        </button>
      );
    }

    const width = 260;
    const gap = 8;
    const left = anchorRect ? Math.max(8, Math.min(window.innerWidth - width - 8, anchorRect.left)) : 8;
    const top = anchorRect ? Math.min(window.innerHeight - 12, anchorRect.bottom + gap) : 60;

    return (
      <>
        <button
          ref={btnRef}
          type="button"
          onClick={toggleOpen}
          className={[
            `w-full sm:w-auto ${minWidthClass}`,
            "px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500",
            "bg-white text-gray-800 flex items-center justify-between gap-2",
          ].join(" ")}
        >
          <span className="truncate">{buttonText}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open &&
          createPortal(
            <div className="fixed inset-0 z-[10002]">
              <div
                ref={panelRef}
                className="absolute w-[260px] rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden"
                style={{ left, top }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div className="px-4 pt-4 pb-3 border-b border-gray-100 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">{placeholder || label || "Filter"}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {selectedCount === 0 ? "Semua dipilih" : `${selectedCount} dipilih`}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={close}
                    className="w-8 h-8 rounded-xl border border-gray-200 inline-flex items-center justify-center text-gray-500 hover:bg-gray-50"
                    title="Tutup"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="px-4 py-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={setAll}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                </div>

                <div className="max-h-[260px] overflow-y-auto px-2 pb-3">
                  {normalizedOptions.map((opt) => {
                    const checked = selectedSet.has(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleValue(opt.value)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }

  /* =========================================================
  * Cancel Modal (Reason required) - responsive
  * ========================================================= */
  function CancelSaleModal({ open, sale, onClose, onSubmit }) {
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (!open) return;
      setReason("");
      setSubmitting(false);
    }, [open]);

    if (!open) return null;

    const saleIdLabel = sale?.sales_id || "-";
    const canSubmit = Boolean(String(reason || "").trim());

    const handleSubmit = async () => {
      if (!canSubmit || submitting) return;
      try {
        setSubmitting(true);
        await onSubmit?.({ saleId: sale?._id, reason: String(reason || "").trim(), sale });
        onClose?.();
      } catch (err) {
        toast.error(err?.message || "Gagal cancel Sales Order");
      } finally {
        setSubmitting(false);
      }
    };

    return createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden max-h-[90vh] flex flex-col">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-200 flex items-start justify-between gap-2 sm:gap-3">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 text-rose-700">
                <Ban className="w-4 h-4" />
                <span className="text-sm font-semibold">Cancel Sales Order</span>
              </div>
              <div className="mt-1 text-sm sm:text-base font-bold text-slate-900 truncate">{saleIdLabel}</div>
              <p className="mt-1 text-xs sm:text-sm text-slate-600">
                Masukkan alasan cancel. Alasan wajib diisi sebelum melanjutkan.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={[
                "w-8 h-8 sm:w-9 sm:h-9 inline-flex items-center justify-center rounded-xl border border-slate-200 flex-shrink-0",
                submitting ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-slate-50",
              ].join(" ")}
              title="Tutup"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          <div className="p-4 sm:p-5 space-y-3 overflow-y-auto flex-1">
            <label className="block text-sm font-semibold text-slate-900">
              Alasan Cancel <span className="text-rose-600">*</span>
            </label>

            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="Contoh: pesanan dibatalkan client, revisi dokumen, salah input, dll."
              className="w-full min-h-[100px] sm:min-h-[120px] rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500"
            />

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className={[
                  "px-4 py-2.5 sm:py-2 rounded-xl border border-slate-200 text-sm font-semibold",
                  submitting ? "text-slate-400 bg-slate-50 cursor-not-allowed" : "text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                Batal
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className={[
                  "px-4 py-2.5 sm:py-2 rounded-xl text-sm font-semibold text-white",
                  !canSubmit || submitting ? "bg-rose-300 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700",
                ].join(" ")}
              >
                {submitting ? "Memproses..." : "Cancel Sales Order"}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }


  /* =========================================================
  * Component - RESPONSIVE
  * ========================================================= */
  export default function SalesOrder() {
    const prevMitraOptionsRef = useRef([]);
const prevProjectOptionsRef = useRef([]);

    const [showForm, setShowForm] = useState(false);
    const [formInitialData, setFormInitialData] = useState(null);


    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [selectedSaleInvoices, setSelectedSaleInvoices] = useState([]);
    const [selectedSaleInvoiceSummary, setSelectedSaleInvoiceSummary] = useState(null);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const buildInvoicesForSalesUrl = (saleId) => `${BASE_URL}/api/invoice-tracker/sales/${saleId}/invoices/restructure`;

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);

    const [showRestructure, setShowRestructure] = useState(false);
    const [restructureSaleId, setRestructureSaleId] = useState(null);
    const [restructureSale, setRestructureSale] = useState(null);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // ✅ Search input (NO suggestions dropdown). Table will show results directly.
    const [q, setQ] = useState("");
    const qDebounced = useDebouncedValue(q, 250);

    const [docType, setDocType] = useState([]); // string[]
    const [status, setStatus] = useState([]); // string[]
    const [mitra, setMitra] = useState([]); // string[]
    const [projectCategory, setProjectCategory] = useState([]); // string[]

    // kept (not used anymore) - so nothing "removed"
    function readMultiSelect(e) {
      return Array.from(e.target.selectedOptions).map((o) => o.value);
    }

    const [showFilters, setShowFilters] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const [menuState, setMenuState] = useState({ saleId: null, rect: null });
    const closeMenu = useCallback(() => setMenuState({ saleId: null, rect: null }), []);

    const openMenu = (saleId, anchorEl) => {
      if (!saleId || !anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      setMenuState((prev) => (prev.saleId === saleId ? { saleId: null, rect: null } : { saleId, rect }));
    };

    const projectCategoryOptions = useMemo(() => {
      const set = new Set();
      (items || []).forEach((s) => {
        const pc = s?.project_category;
        if (pc) set.add(String(pc).trim());
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
    }, [items]);

    const mitraOptions = useMemo(() => {
      const set = new Set();
      (items || []).forEach((s) => {
        const name = s?.client_id?.client_name || s?.client?.client_name;
        if (name) set.add(String(name).trim());
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b, "id"));
    }, [items]);

    // Auto-select all filters by default (once options exist)
    useEffect(() => {
      if (docType.length === 0) setDocType(DOC_TYPE);
      if (status.length === 0) setStatus(SALES_STATUS.map((s) => s.value));

      if (mitra.length === 0 && mitraOptions.length > 0) setMitra(mitraOptions);
      if (projectCategory.length === 0 && projectCategoryOptions.length > 0) setProjectCategory(projectCategoryOptions);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mitraOptions, projectCategoryOptions]);
    useEffect(() => {
  const prev = prevMitraOptionsRef.current || [];
  const next = mitraOptions || [];

  // if previously user effectively had "all selected", keep it "all selected"
  const hadAllSelected =
    mitra.length === prev.length && prev.every((x) => mitra.includes(x));

  if (hadAllSelected && next.length > 0) {
    setMitra(next);
  }

  prevMitraOptionsRef.current = next;
}, [mitraOptions]); // eslint-disable-line react-hooks/exhaustive-deps

useEffect(() => {
  const prev = prevProjectOptionsRef.current || [];
  const next = projectCategoryOptions || [];

  const hadAllSelected =
    projectCategory.length === prev.length && prev.every((x) => projectCategory.includes(x));

  if (hadAllSelected && next.length > 0) {
    setProjectCategory(next);
  }

  prevProjectOptionsRef.current = next;
}, [projectCategoryOptions]); // eslint-disable-line react-hooks/exhaustive-deps

   const filtered = useMemo(() => {
  const qq = q.trim().toLowerCase();
  const mitraArr = Array.isArray(mitra) ? mitra.map((x) => String(x).trim().toLowerCase()).filter(Boolean) : [];
  const pcArr = Array.isArray(projectCategory)
    ? projectCategory.map((x) => String(x).trim().toLowerCase()).filter(Boolean)
    : [];
  const docArr = Array.isArray(docType) ? docType.map((x) => String(x).trim()).filter(Boolean) : [];
  const statusArr = Array.isArray(status) ? status.map((x) => normalizeStatus(x)).filter(Boolean) : [];

  const arr = (items || []).filter((s) => {
    const salesId = String(s?.sales_id || "").toLowerCase();
    const clientNameRaw = s?.client_id?.client_name || s?.client?.client_name || "";
    const clientName = String(clientNameRaw).toLowerCase();

    const refType = String(s?.reference_document_type || "");
    const st = normalizeStatus(s?.status);
    const pc = String(s?.project_category || "").toLowerCase();

    const matchQ = !qq || salesId.includes(qq) || clientName.includes(qq) || st.includes(qq) || pc.includes(qq);
    const matchType = docArr.length === 0 || docArr.includes(refType);
    const matchStatus = statusArr.length === 0 || statusArr.includes(st);
    const matchMitra = mitraArr.length === 0 || mitraArr.includes(clientName);
    const matchProjectCategory = pcArr.length === 0 || pcArr.includes(pc);

    return matchQ && matchType && matchStatus && matchMitra && matchProjectCategory;
  });

  // ✅ earliest at the top
  arr.sort((a, b) => {
    const ta = new Date(a?.creation_date || a?.createdAt || 0).getTime();
    const tb = new Date(b?.creation_date || b?.createdAt || 0).getTime();
    return ta - tb; // ascending = oldest first
  });

  return arr;
}, [items, q, docType, status, mitra, projectCategory]);

    useEffect(() => setPage(1), [q, docType, status, mitra, projectCategory, pageSize]);

    const totalPages = useMemo(
      () => Math.max(1, Math.ceil((filtered?.length || 0) / Math.max(1, pageSize))),
      [filtered, pageSize]
    );

    useEffect(() => setPage((p) => Math.min(Math.max(1, p), totalPages)), [totalPages]);

    const pageItems = useMemo(() => {
      const size = Math.max(1, pageSize);
      const start = (page - 1) * size;
      const end = start + size;
      return (filtered || []).slice(start, end);
    }, [filtered, page, pageSize]);

    const abortRef = useRef(null);
    const lastFetchKeyRef = useRef("");

    // ✅ IMPORTANT FIX:
    // - Do NOT send q to backend when typing.
    // - Fetch list then filter locally.
    const buildFetchKey = useCallback(() => "sales_list_v1", []);

    const loadSales = useCallback(
      async ({ force = false } = {}) => {
        const key = buildFetchKey();

        // allow revalidation even if same key; only block if a fetch is already in-flight
if (abortRef.current && !force) {
  // optional: don't start a second fetch
}

        lastFetchKeyRef.current = key;

        if (!force) {
          const cached = loadSalesCache();
          if (cached?.key === key && Array.isArray(cached?.data)) {
            const age = Date.now() - Number(cached.ts || 0);
           if (cached?.key === key && Array.isArray(cached?.data)) {
  setItems(cached.data);
  setLoading(false);
  // DO NOT return — keep going to fetch & revalidate
}
else {
              setItems(cached.data);
              setLoading(false);
            }
          }
        }

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading((prev) => (items.length ? prev : true));

        try {
          const params = new URLSearchParams();
          params.set("limit", "500");
          params.set("_ts", String(Date.now()));
          const data = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales?${params.toString()}`, {
            signal: controller.signal,
          });

          const arr = Array.isArray(data) ? data : [];
          setItems(arr);
          saveSalesCache({ key, ts: Date.now(), data: arr });
        } catch (err) {
          if (err?.name === "AbortError") return;
          console.error(err);
          toast.error(err?.message || "Gagal memuat Sales Order");
        } finally {
          setLoading(false);
        }
      },
      [buildFetchKey, items.length]
    );

    useEffect(() => {
      loadSales({ force: true });
      return () => {
        if (abortRef.current) abortRef.current.abort();
      };
    }, [loadSales]);

    useEffect(() => {
      const onVisibility = () => {
       if (document.visibilityState === "visible") loadSales({ force: true });

      };
      document.addEventListener("visibilitychange", onVisibility);
      return () => document.removeEventListener("visibilitychange", onVisibility);
    }, [loadSales]);
useEffect(() => {
  const refetch = () => loadSales({ force: true });

  const onVisibility = () => {
    if (document.visibilityState === "visible") refetch();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", refetch);

  return () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", refetch);
  };
}, [loadSales]);

    const handleAdd = () => {
      setFormInitialData(null);
      setSelectedSale(null);
      setShowViewModal(false);
      setShowForm(true);
    };

    const handleDuplicate = async (saleId) => {
  try {
    const sale = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}`);

    const cloned = {
      ...sale,
      _id: undefined,
      sales_id: undefined,
      created_by: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      __v: undefined,
    };

    cloned.status = "unpaid";

 
    setFormInitialData(cloned);
    setSelectedSale(null);
    setShowViewModal(false);
    setShowForm(true);
  } catch (err) {
    console.error(err);
    toast.error(err?.message || "Gagal duplicate Sales Order");
  }
};



    const handleView = async (saleId) => {
      try {
        setLoadingInvoices(true);
        setSelectedSaleInvoices([]);
        setSelectedSaleInvoiceSummary(null);

        const [sale, invPack] = await Promise.all([
          fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}`),
          fetchJson(buildInvoicesForSalesUrl(saleId)),
        ]);

        setSelectedSale(sale);
        setSelectedSaleInvoices(Array.isArray(invPack?.invoices) ? invPack.invoices : []);
        setSelectedSaleInvoiceSummary(invPack?.summary || null);

        setShowViewModal(true);
      } catch (err) {
        console.error(err);

        const fallback = (items || []).find((x) => String(x._id) === String(saleId));
        if (fallback) {
          setSelectedSale(fallback);
          setSelectedSaleInvoices([]);
          setSelectedSaleInvoiceSummary(null);
          setShowViewModal(true);
          toast.info("Menampilkan data ringkas (detail gagal dimuat).");
          return;
        }

        toast.error(err?.message || "Gagal memuat detail Sales Order");
      } finally {
        setLoadingInvoices(false);
      }
    };

    const handleDownloadDoc = async (saleId) => {
      try {
        const url = buildDownloadDocUrl(saleId);
        const blob = await fetchBlob(url, { method: "GET" });

        const row = (items || []).find((x) => String(x._id) === String(saleId));
        const rd = getLatestReferenceDoc(row);
        const storedName = String(rd?.reference_document || "").trim();


        const salesIdStr = String(row?.sales_id || "reference-document").trim();

        const baseName = storedName || `Reference_Document_${salesIdStr}`;
        const safeBase = baseName.replace(/[\\/:*?"<>|]/g, "-");
        const filename = safeBase.toLowerCase().endsWith(".pdf") ? safeBase : `${safeBase}.pdf`;

        downloadBlob(blob, filename);
        toast.success("Dokumen berhasil didownload");
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Gagal download dokumen");
      }
    };

    const handleDownloadInvoiceSentFromModal = async (inv) => {
      const invoiceId = inv?._id || inv?.id;
      if (!invoiceId) throw new Error("Invoice id tidak ditemukan");

      const blob = await fetchBlob(buildDownloadInvoiceSentUrl(invoiceId), { method: "GET" });

      const invoiceNo = String(inv?.invoiceNo || inv?.issued_id || `invoice-${invoiceId}`).trim();
      const filename = `${invoiceNo}_SENT.pdf`.replace(/[\\/:*?"<>|]/g, "-");

      downloadBlob(blob, filename);
    };

    const handleDownloadInvoicePaymentFromModal = async (inv) => {
      const invoiceId = inv?._id || inv?.id;
      if (!invoiceId) throw new Error("Invoice id tidak ditemukan");

      const blob = await fetchBlob(buildDownloadInvoicePaymentUrl(invoiceId), { method: "GET" });

      const invoiceNo = String(inv?.invoiceNo || inv?.issued_id || `invoice-${invoiceId}`).trim();
      const filename = `${invoiceNo}_PAYMENT.pdf`.replace(/[\\/:*?"<>|]/g, "-");

      downloadBlob(blob, filename);
    };

    const handleDownloadReferenceFromModal = async (saleObj) => {
      const sid = saleObj?._id;
      if (!sid) throw new Error("Sale id tidak ditemukan");
      await handleDownloadDoc(sid);
    };

    const handleCreateAdvanceInvoiceFromModal = (saleObj) => {
      toast.info(`TODO: Buat Invoice Uang Muka untuk Sales ${saleObj?.sales_id || "-"}`);
    };

    const openCancel = async (saleId) => {
      try {
        const sale = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}`);
        setCancelTarget(sale);
        setShowCancelModal(true);
      } catch (err) {
        const fallback = (items || []).find((x) => String(x._id) === String(saleId));
        if (fallback) {
          setCancelTarget(fallback);
          setShowCancelModal(true);
          toast.info("Cancel menggunakan data ringkas (detail gagal dimuat).");
          return;
        }
        toast.error(err?.message || "Gagal memuat Sales Order untuk cancel");
      }
    };

    const submitCancel = async ({ saleId, reason, sale }) => {
      if (!saleId) throw new Error("Sale id tidak ditemukan");
      const cleanReason = String(reason || "").trim();
      if (!cleanReason) throw new Error("Alasan cancel wajib diisi");

      await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${saleId}`, {
  method: "PUT",
  body: JSON.stringify({
    status: "cancelled",
    cancel_reason: cleanReason,
    reason: cleanReason, // ✅ required by backend
  }),
});

    toast.success(`Sales berhasil dicancel: ${sale?.sales_id || saleId}`);

      clearSalesCache();
      lastFetchKeyRef.current = "";

      await loadSales({ force: true });

    };

    const handleSubmitSalesOrder = async ({ payload, file }) => {
      try {
        if (!payload || typeof payload !== "object") throw new Error("Payload invalid");

        let saved = null;

        if (formInitialData?._id) {
          saved = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales/${formInitialData._id}`, {
            method: "PUT",
            body: JSON.stringify(payload),
          });
          toast.success("Sales Order berhasil diperbarui");
        } else {
          saved = await fetchJson(`${BASE_URL}/api/invoice-tracker/sales`, {
            method: "POST",
            body: JSON.stringify(payload),
          });
          toast.success("Sales Order berhasil dibuat");
        }

        if (file) {
          const saleId = saved?._id || formInitialData?._id;
          if (!saleId) throw new Error("Sale id tidak ditemukan setelah simpan");

          toast.info("Mengupload dokumen PDF...");
          await uploadSaleProofPdf({ saleId, file });
          toast.success("Dokumen tersimpan (signed URL + commit).");
        }

      setShowForm(false);
      setFormInitialData(null);

      // 🔥 IMPORTANT: invalidate cache + fetch guard
      clearSalesCache();
      lastFetchKeyRef.current = "";

      await loadSales({ force: true });

      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Gagal menyimpan Sales Order");
      }
    };

    const openRestructure = (saleId) => {
      setRestructureSaleId(saleId);
      const row = (items || []).find((x) => String(x?._id) === String(saleId));
      setRestructureSale(row || null);
      setShowRestructure(true);
    };

    const submitRestructure = async (payload) => {
  try {
    const { saleId } = payload || {};
    if (!saleId) throw new Error("saleId missing");

    await fetchJson(buildRestructurePutUrl(saleId), {
      method: "PUT",
      body: JSON.stringify({
        // ✅ include sales edits
        items: payload.items,
        ppn: payload.ppn,
        pph: payload.pph,
        discount: payload.discount,
        subtotal: payload.subtotal,
        total_discount: payload.total_discount,
        total_ppn: payload.total_ppn,
        total_pph: payload.total_pph,
        grand_total: payload.grand_total,

        // ✅ include restructure fields
        installment_percentages: payload.installment_percentages,
        unpaid_due_dates: payload.unpaid_due_dates,
        term_of_payment: payload.term_of_payment,
      }),
    });

    toast.success("Payment restructure berhasil disimpan.");
    setShowRestructure(false);
    setRestructureSaleId(null);

    clearSalesCache();
    lastFetchKeyRef.current = "";
    await loadSales({ force: true });
  } catch (err) {
    console.error(err);
    toast.error(err?.message || "Gagal melakukan payment restructure");
  }
};


    const activeFiltersCount = [docType, status, mitra, projectCategory].reduce(
      (acc, v) => acc + (Array.isArray(v) ? v.length : v ? 1 : 0),
      0
    );

    return (
          <>
    
      <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
        <AdvanceViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedSale(null);
            setSelectedSaleInvoices([]);
            setSelectedSaleInvoiceSummary(null);
          }}
          sales={selectedSale}
          invoices={selectedSaleInvoices}
          invoiceSummary={selectedSaleInvoiceSummary}
          invoicesLoading={loadingInvoices}
          onDownloadReference={handleDownloadReferenceFromModal}
          onDownloadSentDocument={handleDownloadInvoiceSentFromModal}
          onDownloadPaymentDocument={handleDownloadInvoicePaymentFromModal}
          onCreateAdvanceInvoice={handleCreateAdvanceInvoiceFromModal}
        />

        <PaymentRestructureForm
          isOpen={showRestructure}
          saleId={restructureSaleId}
          sale={restructureSale}
          onClose={() => {
            setShowRestructure(false);
            setRestructureSaleId(null);
            setRestructureSale(null);
          }}
          onSubmit={submitRestructure}
        />

        <CancelSaleModal
          open={showCancelModal}
          sale={cancelTarget}
          onClose={() => {
            setShowCancelModal(false);
            setCancelTarget(null);
          }}
          onSubmit={submitCancel}
        />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Penjualan</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Kelola data penjualan</p>
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors text-sm sm:text-base font-medium"
          >
            <Plus className="w-4 h-4" />
            Tambah Penjualan
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-3 sm:p-6 border-b border-gray-200 space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cari Sales ID, Mitra..."
                  className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="sm:hidden flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>Filter</span>
                {activeFiltersCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
              </button>
            </div>

            <div className={`${showFilters ? "flex" : "hidden"} sm:flex flex-col sm:flex-row gap-2 sm:gap-3`}>
              <MultiSelectDropdown
                label="Mitra"
                placeholder="Semua Mitra"
                options={mitraOptions.map((m) => ({ value: m, label: m }))}
                value={mitra}
                onChange={setMitra}
                minWidthClass="sm:min-w-[180px]"
              />

              <MultiSelectDropdown
                label="Project"
                placeholder="Project Category"
                options={projectCategoryOptions.map((pc) => ({ value: pc, label: pc }))}
                value={projectCategory}
                onChange={setProjectCategory}
                minWidthClass="sm:min-w-[180px]"
              />

              <MultiSelectDropdown
                label="Dokumen"
                placeholder="Tipe Dokumen"
                options={DOC_TYPE.map((t) => ({ value: t, label: t }))}
                value={docType}
                onChange={setDocType}
                minWidthClass="sm:min-w-[160px]"
              />

              <MultiSelectDropdown
                label="Status"
                placeholder="Semua Status"
                options={SALES_STATUS.map((s) => ({ value: s.value, label: s.label }))}
                value={status}
                onChange={setStatus}
                minWidthClass="sm:min-w-[160px]"
              />
            </div>

            <div className="text-xs text-gray-500">
              {loading ? "Memuat data..." : q !== qDebounced ? "Mengetik..." : "Data otomatis diperbarui"}
            </div>
          </div>

          {/* =========================================================
          * Desktop Table
          * ========================================================= */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">Sales ID</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600 min-w-[180px]">Mitra</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600 min-w-[160px]">
                    Project Category
                  </th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600 min-w-[120px]">Tanggal</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">Ref Type</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">TOP</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">Grand Total</th>
                  <th className="px-4 xl:px-6 py-3 text-center text-xs font-semibold text-gray-600">Aksi</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-gray-500 text-sm">
                      Memuat data...
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-gray-500 text-sm">
                      Tidak ada Penjualan
                    </td>
                  </tr>
                ) : (
                  pageItems.map((sale) => {
                    const clientName = sale?.client_id?.client_name || sale?.client?.client_name || "-";
                    const saleStatus = normalizeStatus(sale?.status || "unpaid");
                    const isCancelled = saleStatus === "cancelled";
                    const projectCategoryText = String(sale?.project_category || "-");

                    return (
                      <tr key={sale._id} className="hover:bg-gray-50">
                        <td className="px-4 xl:px-6 py-4 text-center">
                          <span className="font-medium text-gray-900 text-sm">{sale?.sales_id || "-"}</span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center text-gray-700 text-sm">
                          <span className="inline-block max-w-[220px] break-words line-clamp-2">{clientName}</span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center text-gray-700 text-sm">
                          <span className="inline-block max-w-[200px] break-words line-clamp-2">{projectCategoryText}</span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center text-gray-700 text-sm whitespace-nowrap">
                          {formatDate(sale?.creation_date)}
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center">
                          <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {sale?.reference_document_type || "-"}
                          </span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center text-gray-700 text-sm">
                          {typeof sale?.term_of_payment === "number" ? sale.term_of_payment : "-"}
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center">
                          <span
                            className={`inline-flex whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadgeClass(
                              saleStatus
                            )}`}
                          >
                            {statusLabel(saleStatus)}
                          </span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center">
                          <span className="font-semibold text-gray-900 text-sm">{formatCurrency(sale?.grand_total)}</span>
                        </td>

                        <td className="px-4 xl:px-6 py-4 text-center">
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleView(sale._id)}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Lihat"
                            >
                              <Eye className="w-4 h-4" />
                            </button>

                            <button
                              type="button"
                              onClick={(e) => openMenu(sale._id, e.currentTarget)}
                              className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                              title="Menu"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            <RowMenuPortal open={menuState.saleId === sale._id} anchorRect={menuState.rect} onClose={closeMenu}>
                              <button
                                type="button"
                                disabled={isCancelled}
                                onClick={() => {
                                  if (isCancelled) return;
                                  closeMenu();
                                  openRestructure(sale._id);
                                }}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                                  isCancelled ? "text-gray-400 cursor-not-allowed" : "text-gray-800 hover:bg-gray-50"
                                }`}
                              >
                                <Wallet className="w-4 h-4 text-amber-600" />
                                Payment Restructure
                              </button>

                              <div className="h-px bg-gray-100" />
                          

                              <button
                                type="button"
                                onClick={() => {
                                  closeMenu();
                                  handleDuplicate(sale._id);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Copy className="w-4 h-4 text-gray-700" />
                                Duplicate
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  closeMenu();
                                  handleDownloadDoc(sale._id);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <FileDown className="w-4 h-4 text-gray-700" />
                                Download Doc
                              </button>

                              <div className="h-px bg-gray-100" />

                              <button
                                type="button"
                                onClick={() => {
                                  closeMenu();
                                  openCancel(sale._id);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                              >
                                <Ban className="w-4 h-4" />
                                Cancel
                              </button>
                            </RowMenuPortal>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* =========================================================
          * Mobile Cards
          * ========================================================= */}
          <div className="lg:hidden divide-y divide-gray-200">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">Memuat data...</div>
            ) : pageItems.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">Tidak ada Sales Order</div>
            ) : (
              pageItems.map((sale) => {
                const clientName = sale?.client_id?.client_name || sale?.client?.client_name || "-";
                const saleStatus = normalizeStatus(sale?.status || "unpaid");
                const isCancelled = saleStatus === "cancelled";
                const projectCategoryText = String(sale?.project_category || "-");

                return (
                  <div key={sale._id} className="p-4 hover:bg-gray-50 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-900 text-base">{sale?.sales_id || "-"}</div>
                        <div className="text-sm text-gray-600 mt-1 break-words">{clientName}</div>
                      </div>
                      <span
                        className={`inline-flex whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${statusBadgeClass(
                          saleStatus
                        )}`}
                      >
                        {statusLabel(saleStatus)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Project Category</div>
                        <div className="text-gray-900 font-medium break-words">{projectCategoryText}</div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mb-1">Tanggal</div>
                        <div className="text-gray-900 font-medium">{formatDate(sale?.creation_date)}</div>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mb-1">Ref Type</div>
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {sale?.reference_document_type || "-"}
                        </span>
                      </div>

                      <div>
                        <div className="text-xs text-gray-500 mb-1">TOP (bulan)</div>
                        <div className="text-gray-900 font-medium">
                          {typeof sale?.term_of_payment === "number" ? sale.term_of_payment : "-"}
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-1">Grand Total</div>
                      <div className="text-base font-bold text-gray-900">{formatCurrency(sale?.grand_total)}</div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => handleView(sale._id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        Lihat
                      </button>

                      <button
                        type="button"
                        onClick={(e) => openMenu(sale._id, e.currentTarget)}
                        className="flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>

                      <RowMenuPortal open={menuState.saleId === sale._id} anchorRect={menuState.rect} onClose={closeMenu}>
                        <button
                          type="button"
                          disabled={isCancelled}
                          onClick={() => {
                            if (isCancelled) return;
                            closeMenu();
                            openRestructure(sale._id);
                          }}
                          className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 ${
                            isCancelled ? "text-gray-400 cursor-not-allowed" : "text-gray-800 hover:bg-gray-50"
                          }`}
                        >
                          <Wallet className="w-4 h-4 text-amber-600" />
                          Payment Restructure
                        </button>


                        <div className="h-px bg-gray-100" />
                    
                        <button
                          type="button"
                          onClick={() => {
                            closeMenu();
                            handleDuplicate(sale._id);
                          }}
                          className="w-full px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4 text-gray-700" />
                          Duplicate
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            closeMenu();
                            handleDownloadDoc(sale._id);
                          }}
                          className="w-full px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 flex items-center gap-2"
                        >
                          <FileDown className="w-4 h-4 text-gray-700" />
                          Download Doc
                        </button>

                        <div className="h-px bg-gray-100" />

                        <button
                          type="button"
                          onClick={() => {
                            closeMenu();
                            openCancel(sale._id);
                          }}
                          className="w-full px-3 py-2.5 text-left text-sm text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                        >
                          <Ban className="w-4 h-4" />
                          Cancel
                        </button>
                      </RowMenuPortal>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* =========================================================
          * Footer Pagination
          * ========================================================= */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap">Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="flex-1 sm:flex-none px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              <span className="text-center sm:text-left">
                Menampilkan <span className="font-semibold">{pageItems.length}</span> dari{" "}
                <span className="font-semibold">{filtered.length}</span> (total:{" "}
                <span className="font-semibold">{items.length}</span>)
              </span>
            </div>

            <div className="flex items-center justify-center gap-2">
              <button
                type="button"
                className="flex-1 sm:flex-none px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>

              <span className="text-xs sm:text-sm text-gray-700 whitespace-nowrap px-2">
                <span className="font-semibold">{page}</span> / <span className="font-semibold">{totalPages}</span>
              </span>

              <button
                type="button"
                className="flex-1 sm:flex-none px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
{showForm && (
  <SelectInvoiceForm
    isOpen={true}
    open={true}
    onClose={() => {
      setShowForm(false);
      setFormInitialData(null);
    }}
    onSubmit={handleSubmitSalesOrder}
    initialData={formInitialData}
  />
)}



    </div>

    </>
  );

    
  }
